import validate from 'validate.js';
import isEmpty from 'lodash/isEmpty';
import { IKeyValue, getObjectWithoutUndefinedPropsFrom } from 'utilitify';

import {
  makeConstraintsForRequired,
  requiredValidation,
  validateWithSingleError,
} from '@crm-framework/validation';
import { makeRequiredConstrainsWithCustomName } from '@crm-framework/validation/src/utils';
import { capitalize } from '@crm-framework/utils';

import {
  BIZ_GROUP,
  CONVERSION_STATUS,
  COUNTRY,
  LANGUAGE,
  REJECT_REASON_FOR_NOT_VALID,
  REJECT_REASON_FOR_NO_POTENTIAL,
  STATUS_NOT_VALID,
  STATUS_NO_POTENTIAL,
  CONTACT_TIME,
  EMAIL,
} from './consts';
import { ILeadPhoneData, ILeadResponseModel } from './types';

export const REQUIRED_FIELDS = [
  EMAIL,
  COUNTRY,
  LANGUAGE,
  BIZ_GROUP,
  CONVERSION_STATUS,
];

export const MAXIMUM_LENGTH = 256;

export const PHONE_FIELDS = [
  'country',
  'operator',
  'invisiblePart',
  'visiblePart',
];

export const REQUIRED_FIELD_NAMES_MAP = {
  businessUnitId: 'Biz Group',
  salesStatusId: 'Conversion Status',
  countryId: 'Country',
  languageId: 'Language',
  phone1: 'Phone 1',
};

const MAXIMUM_LENGTH_FORM_FIELD: {
  [key in keyof Partial<ILeadResponseModel>]: number;
} = {
  firstName: 200,
  lastName: 200,
  city: 64,
};

const makeNumericalityConstraints = (fieldArray: string[]) =>
  fieldArray.reduce((result, field) => {
    result[field] = { numericality: true };

    return result;
  }, {});

const checkIsAllPhoneEmpty = (phone: ILeadPhoneData) => {
  const values = Object.values(phone);

  return (
    values.length === PHONE_FIELDS.length && values.every((value) => !value)
  );
};

const phoneValidation = (
  field: ILeadPhoneData,
  name: string
): string | string[] | undefined => {
  if (name !== 'phone1' && checkIsAllPhoneEmpty(field)) {
    return undefined;
  }

  if (checkIsAllPhoneEmpty(field)) return 'Phone 1 is required.';

  let fieldNames = Object.keys(field);
  fieldNames = fieldNames.filter(
    (fieldName) => fieldName !== 'phoneVerification' && fieldName !== 'details'
  );

  let errors = validate(field, makeConstraintsForRequired(PHONE_FIELDS));

  if (!isEmpty(errors)) return 'All fields must be filled.';

  if (
    fieldNames.includes('invisiblePart') &&
    field.invisiblePart &&
    !new RegExp('^[0-9*]{4}$').exec(field.invisiblePart)
  ) {
    return 'The last field should contain 4 digits.';
  }

  errors = validate(
    field,
    makeNumericalityConstraints(fieldNames.filter((n) => n !== 'invisiblePart'))
  );

  if (!isEmpty(errors)) return 'All fields must contain only digits.';

  return undefined;
};

export const requiredNumbersValidation = (
  fields: IKeyValue<string>
): IKeyValue<string> | undefined => {
  const requiredErrors: ReturnType<typeof requiredNumbersValidation> = validate(
    { ...fields },
    makeConstraintsForRequired(Object.keys(fields))
  );

  if (requiredErrors) return requiredErrors;

  const numericalityConstraints = makeNumericalityConstraints(
    Object.keys(fields)
  );
  const typesValidation: ReturnType<typeof requiredNumbersValidation> =
    validate({ ...fields }, numericalityConstraints);

  if (typesValidation) return typesValidation;

  return undefined;
};

export const validateRequiredFields = (
  fields: Partial<ILeadResponseModel>,
  salesStatusName: string,
  needToValidateRejectReason: boolean
) => {
  const fieldNames = Object.keys(fields);

  return (
    fieldNames.reduce((acc, name: string) => {
      if (name.includes('phone1')) {
        const errors = phoneValidation(fields[name], name);

        if (errors) {
          acc[name] = errors;
        }
      }

      if (name.includes(CONTACT_TIME)) {
        let errors;

        if (
          fields.contactTime &&
          new Date(fields.contactTime) < new Date('1970-01-01 00:00')
        ) {
          errors = 'Contact time must be more than 1969 year';
        }

        if (
          fields.contactTime &&
          new Date(fields.contactTime) > new Date('2050-12-31 23:59')
        ) {
          errors = 'Contact time must be less than 2051 year';
        }

        if (errors) {
          acc[name] = errors;
        }
      }

      if (REQUIRED_FIELDS.includes(name)) {
        const constrains = makeRequiredConstrainsWithCustomName(
          makeConstraintsForRequired([name]),
          {
            [name]: REQUIRED_FIELD_NAMES_MAP[name] || name,
          }
        );
        const error = validate({ [name]: fields[name] }, constrains);

        if (error) {
          acc[name] = error[name];
        }
      }

      if (
        Object.keys(MAXIMUM_LENGTH_FORM_FIELD).includes(name) &&
        fields[name]?.length > MAXIMUM_LENGTH_FORM_FIELD[name]
      ) {
        acc[name] = `${
          name.charAt(0).toUpperCase() + name.slice(1)
        } cannot be more than ${MAXIMUM_LENGTH_FORM_FIELD[name]} characters`;
      }

      if (needToValidateRejectReason) {
        if (
          salesStatusName === STATUS_NOT_VALID &&
          !fields[REJECT_REASON_FOR_NOT_VALID]
        ) {
          acc[REJECT_REASON_FOR_NOT_VALID] = 'Reject Reason is required';
        }

        if (
          salesStatusName === STATUS_NO_POTENTIAL &&
          !fields[REJECT_REASON_FOR_NO_POTENTIAL]
        ) {
          acc[REJECT_REASON_FOR_NO_POTENTIAL] = 'Reject Reason is required';
        }
      }

      return acc;
    }, {}) || {}
  );
};

export const contactDataValidation = (field: string, value: string) => {
  const errors: { [key: string]: string | undefined } = {};

  const constraints = {
    email: requiredValidation('Email'),
  };

  switch (field) {
    case EMAIL: {
      errors.email = validateWithSingleError(value, constraints.email);

      if (value && !/\S+@\S+\.\S+/.test(value || '')) {
        errors.email = 'The Email field is not a valid e-mail address.';
      }

      break;
    }
    default: {
      if (value && value.length > 64) {
        errors[field] = `${capitalize(
          field
        )} cannot be more than 64 characters`;
      }
    }
  }

  return getObjectWithoutUndefinedPropsFrom(errors);
};
